import type { userProfileStore } from '../infrastructure/store'
import type userProfileApiClient from '../infrastructure/userProfileApiClient'

function userProfileUseCases({
  apiClient,
  store,
  invalidateUserQuery,
}: {
  apiClient: typeof userProfileApiClient
  store: typeof userProfileStore
  invalidateUserQuery: () => void
}) {
  let bannerTimer: ReturnType<typeof setTimeout> | null = null

  async function pageLoaded() {
    const state = store.getState()
    state.setIn('retrieveStatus.loading', true)

    const [profileResult, customerStatusResult] = await Promise.allSettled([
      apiClient.getUserProfile(),
      apiClient.getCustomerStatus(),
    ])
    if (profileResult.status === 'rejected' || !profileResult.value) {
      state.setIn('retrieveStatus', { loading: false, error: true })
      return
    }

    const isAccountDeletable = customerStatusResult.status === 'fulfilled'
      ? customerStatusResult.value
      : false
    state.initialize({
      birthDate: profileResult.value.birthDate,
      firstName: profileResult.value.firstName,
      lastName: profileResult.value.lastName,
      birthDateUnformatted: profileResult.value.birthDateUnformatted,
      isAccountDeletable,
      retrieveStatus: { loading: false, error: false },
      saveStatus: { loading: false, error: false, success: false },
      editing: false,
    })
  }

  async function updateProfile({ firstName, lastName, birthDate }: { firstName: string, lastName: string, birthDate: string }) {
    const state = store.getState()
    state.setIn('saveStatus.success', false)
    state.setIn('saveStatus.error', false)
    const hasFirstNameChanged = firstName.trim() !== state.firstName
    const hasLastNameChanged = lastName.trim() !== state.lastName
    const hasBirthDateChanged = birthDate.trim() !== state.birthDateUnformatted

    if (!hasFirstNameChanged && !hasLastNameChanged && !hasBirthDateChanged) {
      state.setEditing(false)
      return
    }

    state.setIn('saveStatus.loading', true)

    const payload: Record<string, string> = {}
    if (hasFirstNameChanged)
      payload.firstName = firstName
    if (hasLastNameChanged)
      payload.lastName = lastName
    if (hasBirthDateChanged)
      payload.birthDate = birthDate

    try {
      const result = await apiClient.updateUserProfile(payload)

      if (hasFirstNameChanged)
        state.setFirstName(firstName)
      if (hasLastNameChanged)
        state.setLastName(lastName)
      if (hasBirthDateChanged)
        state.setBirthDate(result.birthDate, result.birthDateUnformatted)

      state.setIn('saveStatus.success', true)

      if (hasFirstNameChanged || hasLastNameChanged) {
        invalidateUserQuery()
      }
    }
    catch {
      state.setIn('saveStatus.error', true)
    }
    finally {
      state.setIn('saveStatus.loading', false)
      state.setEditing(false)

      if (bannerTimer)
        clearTimeout(bannerTimer)
      bannerTimer = setTimeout(() => {
        state.setIn('saveStatus.success', false)
        state.setIn('saveStatus.error', false)
        bannerTimer = null
      }, 5000)
    }
  }

  async function deleteAccount() {
    const state = store.getState()

    if (state.deleteStatus?.loading)
      return

    state.setIn('isDeleteAlertOpen', false)
    state.setIn('deleteStatus.loading', true)
    state.setIn('deleteStatus.success', false)
    state.setIn('deleteStatus.error', false)
    try {
      const success = await apiClient.deleteAccount()
      if (success) {
        state.setIn('deleteStatus.success', true)
      }
      else {
        state.setIn('deleteStatus.error', true)
      }
    }
    catch {
      state.setIn('deleteStatus.error', true)
    }
    finally {
      state.setIn('deleteStatus.loading', false)
      if (bannerTimer)
        clearTimeout(bannerTimer)
      bannerTimer = setTimeout(() => {
        state.setIn('deleteStatus.error', false)
        bannerTimer = null
      }, 5000)
    }
  }

  return {
    pageLoaded,
    updateProfile,
    deleteAccount,
  }
}

export default userProfileUseCases
